export function convertDataFormat(data) {
    return data.map(item => {
        // Parse dates from ISO string format
        const startDate = item.start_date
        const endDate = item.end_date
        
        return {
            id: item._id,
            _id: item._id,
            userId: item.userId,
            title: item.objective,
            category: item.category,
            objective: item.objective,
            kpi: item.kpi,
            start: startDate,
            end: endDate,
            description: item.notes, // Using objective as description since it's not in original data
            notes: item.notes,
            timeline: {
                startDate: startDate,
                endDate: endDate
            },
            completed: item.completed || false,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt
        };
    });
}

export function getStreamsDataForMonth(data, month, year) {
  // Month name to number mapping
  const monthMap = {
    'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
    'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
  };
  
  // Get month number from abbreviation
  const targetMonthNum = monthMap[month];
  const targetYear = parseInt(year);
  
  if (!targetMonthNum) {
    return {
      success: false,
      message: "Invalid month format. Please use 3-letter abbreviation (e.g., 'Jan', 'Feb')",
      data: null
    };
  }
  
  // Ensure data is an array
  const arr = Array.isArray(data) ? data : [];
  // Find the exact month/year match
  const targetEntry = arr.find(entry => {
    const entryDate = new Date(entry.date);
    const entryMonth = entryDate.getMonth() + 1; // 0-indexed, so add 1
    const entryYear = entryDate.getFullYear();
    return entryMonth === targetMonthNum && entryYear === targetYear;
  });
  
  // If found and has non-zero streams, return it
  if (targetEntry && targetEntry.streams_total > 0) {
    return {
      success: true,
      month,
      year: targetYear,
      date: targetEntry.date,
      streams_total: targetEntry.streams_total,
      frequency: targetEntry.frequency,
      isFallback: false
    };
  }
  
  // If not found or streams_total is 0, look for previous months with data
  const sortedData = [...arr]
    .filter(entry => entry.streams_total > 0) // Only consider entries with data
    .sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort descending by date
  
  // Create target date for comparison
  const targetDate = new Date(targetYear, targetMonthNum - 1, 1);
  
  // Find the most recent entry before the target date with non-zero streams
  const fallbackEntry = sortedData.find(entry => {
    const entryDate = new Date(entry.date);
    return entryDate <= targetDate;
  });
  
  if (fallbackEntry) {
    const fallbackDate = new Date(fallbackEntry.date);
    const fallbackMonth = fallbackDate.toLocaleString('en-US', { month: 'short' });
    const fallbackYear = fallbackDate.getFullYear();
    
    return {
      success: true,
      requestedMonth: month,
      requestedYear: targetYear,
      month: fallbackMonth,
      year: fallbackYear,
      date: fallbackEntry.date,
      streams_total: fallbackEntry.streams_total,
      frequency: fallbackEntry.frequency,
      isFallback: true,
      message: `No data for ${month} ${targetYear}. Showing data from ${fallbackMonth} ${fallbackYear}`
    };
  }
  
  // No data found at all
  return {
    success: false,
    message: `No stream data available for ${month} ${targetYear} or any previous months`,
    data: null
  };
}

// export function calculateHighestCategoryROI(revenueData, expenseData) {
//   // Group revenue by category
//   const categoryRevenue = {};
//   revenueData.forEach(item => {
//     const category = item.category;
//     const amount = parseFloat(item.amount || 0);
//     categoryRevenue[category] = (categoryRevenue[category] || 0) + amount;
//   });
  
//   // Group expenses by category
//   const categoryExpense = {};
//   expenseData.forEach(item => {
//     const category = item.category;
//     const amount = parseFloat(item.amount || 0);
//     categoryExpense[category] = (categoryExpense[category] || 0) + amount;
//   });
  
//   // Get all unique categories
//   const allCategories = new Set([
//     ...Object.keys(categoryRevenue),
//     ...Object.keys(categoryExpense)
//   ]);
  
//   // Calculate ROI for each category (only where both revenue and expense > 0)
//   const categoryROI = [];
  
//   allCategories.forEach(category => {
//     const revenue = categoryRevenue[category] || 0;
//     const expense = categoryExpense[category] || 0;
    
//     // Only calculate ROI if both revenue and expense are greater than 0
//     if (revenue > 0 && expense > 0) {
//       const profit = revenue - expense;
//       const roi = profit / expense;
//       const roiPercentage = roi * 100;
      
//       categoryROI.push({
//         category,
//         revenue: parseFloat(revenue.toFixed(2)),
//         expense: parseFloat(expense.toFixed(2)),
//         profit: parseFloat(profit.toFixed(2)),
//         roi: parseFloat(roi.toFixed(4)),
//         roiPercentage: parseFloat(roiPercentage.toFixed(2)),
//         formatted: `${roiPercentage >= 0 ? '+' : ''}${roiPercentage.toFixed(2)}%`
//       });
//     }
//   });
  
//   // Check if any valid ROI was calculated
//   if (categoryROI.length === 0) {
//     return {
//       success: false,
//       message: "No categories with both revenue and expenses found",
//       highestROICategory: null,
//       allCategories: []
//     };
//   }
  
//   // Find the category with the highest ROI
//   const highestROICategory = categoryROI.reduce((max, current) => {
//     return current.roi > max.roi ? current : max;
//   });
  
//   // Sort all categories by ROI (descending)
//   const sortedCategories = categoryROI.sort((a, b) => b.roi - a.roi);
  
//   return {
//     success: true,
//     highestROICategory,
//     allCategories: sortedCategories,
//     totalCategoriesAnalyzed: sortedCategories.length
//   };
// }

export function calculateHighestCategoryROI(revenueData, expenseData) {
  // Validate input data
  if ((!revenueData || revenueData.length === 0) && (!expenseData || expenseData.length === 0)) {
    return {
      success: false,
      message: "No revenue or expense data available",
      highestROICategory: null,
      allCategories: []
    };
  }

  // Group revenue by category
  const categoryRevenue = {};
  if (revenueData && revenueData.length > 0) {
    revenueData.forEach(item => {
      const category = item.category;
      const amount = parseFloat(item.amount || 0);
      if (amount > 0) {
        categoryRevenue[category] = (categoryRevenue[category] || 0) + amount;
      }
    });
  }
  
  // Group expenses by category
  const categoryExpense = {};
  if (expenseData && expenseData.length > 0) {
    expenseData.forEach(item => {
      const category = item.category;
      const amount = parseFloat(item.amount || 0);
      if (amount > 0) {
        categoryExpense[category] = (categoryExpense[category] || 0) + amount;
      }
    });
  }
  
  // Get all unique categories
  const allCategories = new Set([
    ...Object.keys(categoryRevenue),
    ...Object.keys(categoryExpense)
  ]);
  
  // Calculate ROI for each category
  const categoryROI = [];
  
  allCategories.forEach(category => {
    const revenue = categoryRevenue[category] || 0;
    const expense = categoryExpense[category] || 0;
    
    // Calculate metrics for all categories with either revenue or expense
    if (revenue > 0 || expense > 0) {
      const profit = revenue - expense;
      
      // Calculate ROI
      // If expense is 0 but revenue exists, ROI is infinite (100% return)
      // If revenue is 0 but expense exists, ROI is -100% (total loss)
      let roi = 0;
      let roiPercentage = 0;
      
      if (expense > 0) {
        roi = profit / expense;
        roiPercentage = roi * 100;
      } else if (revenue > 0) {
        // No expense, pure profit - assign very high ROI
        roi = Infinity;
        roiPercentage = Infinity;
      }
      
      categoryROI.push({
        category,
        revenue: parseFloat(revenue.toFixed(2)),
        expense: parseFloat(expense.toFixed(2)),
        profit: parseFloat(profit.toFixed(2)),
        roi: roi === Infinity ? Infinity : parseFloat(roi.toFixed(4)),
        roiPercentage: roiPercentage === Infinity ? Infinity : parseFloat(roiPercentage.toFixed(2)),
        formatted: roiPercentage === Infinity 
          ? '∞%' 
          : `${roiPercentage >= 0 ? '+' : ''}${roiPercentage.toFixed(2)}%`
      });
    }
  });
  
  // Check if any valid category was found
  if (categoryROI.length === 0) {
    return {
      success: false,
      message: "No valid categories found with revenue or expenses",
      highestROICategory: null,
      allCategories: []
    };
  }
  
  // Sort all categories by ROI (descending)
  // Handle Infinity values properly
  const sortedCategories = categoryROI.sort((a, b) => {
    // Infinity should come first
    if (a.roi === Infinity && b.roi === Infinity) return 0;
    if (a.roi === Infinity) return -1;
    if (b.roi === Infinity) return 1;
    return b.roi - a.roi;
  });
  
  // The highest ROI category is the first one after sorting
  const highestROICategory = sortedCategories[0];
  
  return {
    success: true,
    highestROICategory,
    allCategories: sortedCategories,
    totalCategoriesAnalyzed: sortedCategories.length
  };
}

export function getDateRangeFromDuration(duration) {
    const now = new Date();

    // End date = last day of current month
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    // Start date = first day of (current month - duration)
    const startDate = new Date(now.getFullYear(), now.getMonth() - duration + 1, 1);

    // Format date as DD-MM-YYYY
    const format = (date) => {
        const d = String(date.getDate()).padStart(2, "0");
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const y = date.getFullYear();
        return `${d}-${m}-${y}`;
    };

    return {
        start_date: format(startDate),
        end_date: format(endDate)
    };
}

export function timeAgo(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  const intervals = {
    year: 365 * 24 * 60 * 60,
    month: 30 * 24 * 60 * 60,
    week: 7 * 24 * 60 * 60,
    day: 24 * 60 * 60,
    hour: 60 * 60,
    minute: 60,
    second: 1,
  };

  if (seconds < 5) return "just now";

  for (const key in intervals) {
    const value = Math.floor(seconds / intervals[key]);
    if (value >= 1) {
      if (key === "day" && value === 1) return "yesterday";
      return value === 1 ? `${value} ${key} ago` : `${value} ${key}s ago`;
    }
  }
}

export function toMinSec(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  const mm = String(mins).padStart(2, "0");
  const ss = String(secs).padStart(2, "0");

  return `${mm}:${ss}`;
}

export const generateChatHistoryRanges = (days = 7) => {
  const ranges = [];

  const today = new Date();

  // Today
  ranges.push({
    label: "Today",
    value: "today",
    date: new Date(today),
  });

  // Yesterday
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  ranges.push({
    label: "Yesterday",
    value: "yesterday",
    date: yesterday,
  });

  // Previous daily dates (up to N days before today)
  for (let i = 2; i <= days; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);

    ranges.push({
      label: d.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
      }),
      value: d.toISOString().split("T")[0], // YYYY-MM-DD
      date: d,
    });
  }

  return ranges;
};
